import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import { ArrowLeft, Package, Clock, ShoppingCart, Hammer, Wrench, Truck, CheckCircle, Plus, Save, Loader } from 'lucide-react';
import ProjectWorkflow from './ProjectWorkflow';
import toast from 'react-hot-toast';

const ProjectExecution = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [progressNote, setProgressNote] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Buscar dados do projeto
  const { data: projectData, isLoading: loadingProject } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProjectById(projectId)
  });

  // Buscar dados do workflow
  const { data: stagesData, isLoading: loadingStages } = useQuery({
    queryKey: ['project-stages', projectId],
    queryFn: () => projectsService.getProjectStages(projectId)
  });

  // Buscar progresso registrado
  const { data: progressData, isLoading: loadingProgress } = useQuery({
    queryKey: ['project-progress', projectId],
    queryFn: () => projectsService.getProjectProgress(projectId)
  });

  // Mutation para registrar progresso
  const recordProgressMutation = useMutation({
    mutationFn: (data) => projectsService.recordStageProgress(projectId, data),
    onSuccess: () => {
      toast.success('Progresso registrado!');
      setProgressNote('');
      setProgressPercentage(0);
      queryClient.invalidateQueries(['project-progress', projectId]);
    },
    onError: () => {
      toast.error('Erro ao registrar progresso');
    }
  });

  const project = projectData?.data;
  const currentStage = project?.current_stage || 'PLANNING';
  const progressEntries = progressData?.data?.entries || [];

  if (loadingProject || loadingStages || loadingProgress) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/projetos')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Execução do Projeto</h1>
          <p className="text-gray-600">
            {project?.project_code} - {project?.project_name || project?.client_name}
          </p>
        </div>
      </div>

      {/* Workflow */}
      <div className="mb-6">
        <ProjectWorkflow
          projectId={projectId}
          currentStage={currentStage}
          onStageChange={(newStage) => {
            queryClient.invalidateQueries(['project', projectId]);
          }}
        />
      </div>

      {/* Registro de Progresso */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Registrar Progresso
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Porcentagem de Conclusão
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={progressPercentage}
              onChange={(e) => setProgressPercentage(parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600">{progressPercentage}%</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição do Progresso
            </label>
            <textarea
              value={progressNote}
              onChange={(e) => setProgressNote(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg"
              rows={3}
              placeholder="Descreva o que foi feito nesta etapa..."
            />
          </div>

          <button
            onClick={() => recordProgressMutation.mutate({
              description: progressNote,
              progress_percentage: progressPercentage
            })}
            disabled={recordProgressMutation.isPending || !progressNote.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {recordProgressMutation.isPending ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Registrar Progresso
          </button>
        </div>
      </div>

      {/* Histórico de Progresso */}
      {progressEntries.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Histórico de Progresso
          </h3>
          <div className="space-y-3">
            {progressEntries.map((entry, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{entry.stage}</span>
                    <span className="text-sm text-gray-500">{entry.progress_percentage}%</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(entry.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info do Projeto */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Informações do Projeto</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-blue-600">Cliente:</span> {project?.client_name}
          </div>
          <div>
            <span className="text-blue-600">Status:</span> {project?.status}
          </div>
          <div>
            <span className="text-blue-600">Etapa atual:</span> {currentStage}
          </div>
          <div>
            <span className="text-blue-600">Criado em:</span> {project?.created_at ? new Date(project.created_at).toLocaleDateString('pt-BR') : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectExecution;